import { STATUS, type StatusId } from '../content/tags';
import type { StatusApply } from '../content/actions';
import type { World } from './world';
import type { AnyUnit, StatusInst } from './types';
import { dealDamage, heal } from './combat';

export function hasStatus(u: AnyUnit, id: StatusId): boolean {
  for (const s of u.statuses) if (s.id === id) return true;
  return false;
}

export function stacksOf(u: AnyUnit, id: StatusId): number {
  for (const s of u.statuses) if (s.id === id) return s.stacks;
  return 0;
}

export function removeStatus(world: World, u: AnyUnit, id: StatusId): void {
  const i = u.statuses.findIndex((s) => s.id === id);
  if (i >= 0) {
    u.statuses.splice(i, 1);
    if (id === 'shield') u.shield = 0;
    world.events.push({ t: 'status', id: u.id, status: id, on: false });
  }
}

/** Applies a status; power for damaging statuses is derived from the source. */
export function applyStatus(world: World, target: AnyUnit, apply: StatusApply, source: AnyUnit | null, sourcePower: number): void {
  if (target.dead) return;
  let id = apply.id;
  const def = STATUS[id];
  if (!def) return;
  // keystone: every debuff also chills
  const srcPlayer = source && source.kind === 'player' ? source : null;
  if (srcPlayer && def.kind === 'debuff' && id !== 'chill' && srcPlayer.flags.has('allChill') && id !== 'stun' && id !== 'taunt') {
    applyStatus(world, target, { id: 'chill', duration: apply.duration, stacks: 1 }, source, sourcePower);
  }
  if (def.kind === 'debuff' && target.kind === 'player' && (id === 'stun' || id === 'taunt')) {
    // players are stunned for a shorter time
    apply = { ...apply, duration: apply.duration * 0.6 };
  }
  let duration = apply.duration;
  if (source) {
    if (def.kind === 'debuff') {
      duration *= source.stats.statusPower;
      if (srcPlayer && srcPlayer.flags.has('curseAmplify') && def.tag === 'curse') duration *= 1.3;
    } else {
      duration *= source.stats.duration;
    }
  }
  const until = world.tick + Math.max(1, Math.round(duration * 20));
  let power = 0;
  if (def.dps) power = sourcePower * (apply.power ?? def.dps) * (source ? source.stats.statusPower : 1);
  if (id === 'shield') {
    power = Math.round(target.maxHp * (apply.power ?? 0.2) * (source ? source.stats.auraPower : 1));
  }
  const stacks = apply.stacks ?? 1;
  let inst = target.statuses.find((s) => s.id === id);
  if (!inst) {
    inst = { id, stacks: Math.min(def.maxStacks, stacks), until, power, source: source?.id ?? 0 };
    target.statuses.push(inst);
    world.events.push({ t: 'status', id: target.id, status: id, on: true });
  } else {
    inst.stacks = Math.min(def.maxStacks, inst.stacks + stacks);
    inst.until = def.stack === 'extend' ? inst.until + Math.round(duration * 20 * 0.5) : Math.max(inst.until, until);
    inst.power = Math.max(inst.power, power);
    if (source) inst.source = source.id;
  }
  if (id === 'shield') target.shield = Math.max(target.shield, power);
  if (id === 'invuln') {
    // handled through invulnUntil for players; mobs use the status directly
  }
}

export function speedMul(u: AnyUnit): number {
  let m = 1;
  for (const s of u.statuses) {
    const def = STATUS[s.id];
    if (def.speedMul !== undefined) {
      if (def.speedMul === 0) return 0;
      m *= Math.pow(def.speedMul, s.stacks);
    }
  }
  return m;
}

export function dmgTakenMul(u: AnyUnit): number {
  let m = 1;
  for (const s of u.statuses) {
    const def = STATUS[s.id];
    if (def.dmgTakenMul) m *= def.dmgTakenMul;
  }
  return m;
}

export function dmgDealtMul(u: AnyUnit): number {
  let m = 1;
  for (const s of u.statuses) {
    const def = STATUS[s.id];
    if (def.dmgDealtMul) m *= def.dmgDealtMul;
  }
  return m;
}

export function armorAdd(u: AnyUnit): number {
  let a = 0;
  for (const s of u.statuses) {
    const def = STATUS[s.id];
    if (def.armorAdd) a += def.armorAdd;
  }
  return a;
}

export function isStunned(u: AnyUnit): boolean {
  return hasStatus(u, 'stun');
}

/** Applies damage-over-time, regeneration and expiry. */
export function tickStatuses(world: World, u: AnyUnit): void {
  if (u.statuses.length === 0) return;
  const dt = world.dt;
  for (let i = u.statuses.length - 1; i >= 0; i--) {
    const s: StatusInst = u.statuses[i];
    const def = STATUS[s.id];
    if (world.tick >= s.until) {
      u.statuses.splice(i, 1);
      if (s.id === 'shield') u.shield = 0;
      world.events.push({ t: 'status', id: u.id, status: s.id, on: false });
      continue;
    }
    if (def.dps && s.power > 0) {
      // apply DoT in 0.5s chunks so damage numbers stay readable
      if (world.tick % 10 === 0) {
        const src = world.unit(s.source) ?? null;
        let dmg = s.power * s.stacks * 0.5;
        if (s.id === 'bleed' && (Math.abs(u.vx) > 5 || Math.abs(u.vy) > 5)) dmg *= 1.5;
        dealDamage(world, src, u, dmg, def.tag ? [def.tag] : [], { isDot: true });
        if (u.dead) return;
      }
    }
    if (def.regenPct && world.tick % 10 === 0) {
      heal(world, u, u.maxHp * def.regenPct * s.stacks * 0.5, null);
    }
  }
}
